/**
 * The admin page's activity log, in the words of its filter: one Catalan label per kind of action
 * the platform records, grouped by area.
 *
 * The sentence a row reads as is written by the platform (`ActivityText`, shared with the profile's
 * history, so a thing never reads two ways) and composed with the account's name by
 * `admin/activitySentence.ts`. What a person picks in «Tipus d'acció» is here.
 * `web/src/generated/activity-kinds.json` is every kind the platform records, read from its sources
 * by `ActivityKindsTest`, and `ca-activity.test.ts` holds each to a label: a kind added there with
 * none here fails that test, not a person's screen. A kind this file does not know is shown as
 * `kindLabel`'s null: a neutral line with the key in small print, never the key alone.
 */

/** The areas, in the order the filter lists them. */
export const ACTIVITY_AREAS: Record<string, string> = {
  account: 'Comptes',
  auth: 'Entrades i sortides',
  invite: 'Invitacions',
  mission: 'Missions',
  campaign: 'Campanyes',
  session: 'Sessions',
  server: 'Servidors',
  test: 'Fitxes de prova',
  recipe: 'Receptes',
  terrain: 'Terrenys',
  catalog: 'Catàleg',
  reports: 'Informes',
  roadmap: 'Full de ruta',
  settings: 'Configuració',
  changelog: 'Novetats',
  watcher: 'Vigilant de pantalla',
  addon: 'Mod del servidor',
};

/** Areas that read as another one: a role or an identity belongs to the accounts. */
const AREA_ALIASES: Record<string, string> = { role: 'account', identity: 'account' };

export const KIND_LABELS: Record<string, string> = {
  'account.anonymised': 'Compte anonimitzat',
  'account.disabled': 'Compte desactivat',
  'account.enabled': 'Compte tornat a activar',
  'account.registered': 'Compte creat',
  'addon.install': 'Mod instal·lat al servidor',
  'auth.device_revoked': 'Sessió oberta tancada des d’un altre dispositiu',
  'auth.password_changed': 'Contrasenya canviada',
  'auth.sign_in_failed': 'Entrada fallida',
  'auth.sign_in_throttled': 'Entrada aturada per massa intents',
  'auth.signed_in': 'Entrada a la plataforma',
  'auth.signed_out': 'Sortida de la plataforma',
  'campaign.chapter': 'Capítol afegit a una campanya',
  'campaign.copied': 'Campanya copiada',
  'campaign.created': 'Campanya creada',
  'campaign.finished': 'Campanya acabada',
  'catalog.scan_done': 'Exploració del catàleg acabada',
  'catalog.scan_started': 'Exploració del catàleg començada',
  'changelog.publish': 'Novetats publicades al Discord',
  'identity.linked': 'Identitat vinculada',
  'identity.unlinked': 'Identitat desvinculada',
  'invite.created': 'Invitació creada',
  'invite.redeemed': 'Invitació usada',
  'invite.revoked': 'Invitació retirada',
  'mission.cover': 'Coberta de missió canviada',
  'mission.deleted': 'Missió enviada a la paperera',
  'mission.editor_added': 'Editor afegit a una missió',
  'mission.editor_invited': 'Invitació a editar una missió',
  'mission.editor_removed': 'Editor tret d’una missió',
  'mission.folder_archived': 'Carpeta de missió arxivada',
  'mission.folder_removed': 'Carpeta de missió retirada del servidor',
  'mission.forked': 'Missió bifurcada',
  'mission.generated': 'Missió generada',
  'mission.purged': 'Missió esborrada per sempre',
  'mission.regenerated': 'Missió tornada a generar',
  'mission.restored': 'Missió recuperada de la paperera',
  'mission.saved': 'Missió desada',
  'mission.smokeCopy': 'Còpia de missió per a una nit de prova',
  'mission.visibility': 'Visibilitat de missió canviada',
  'recipe.cards_required': 'Fitxes de prova d’una recepta fixades',
  'recipe.state': 'Estat d’una recepta canviat',
  'reports.deleted': 'Informe esborrat',
  'reports.regenerated': 'Informe regenerat',
  'reports.synced': 'Informes sincronitzats',
  'roadmap.comment': 'Comentari al full de ruta',
  'roadmap.proposal': 'Proposta al full de ruta',
  'role.granted': 'Rol donat',
  'role.revoked': 'Rol retirat',
  'server.configured': 'Servidor configurat',
  'server.created': 'Servidor creat',
  'server.deleted': 'Servidor esborrat',
  'server.respawn': 'Reaparició per defecte d’un servidor canviada',
  'server.flight_model': 'Model de vol d’un servidor canviat',
  'server.linked': 'Servidor enllaçat',
  'server.mission_switched': 'Missió d’un servidor canviada',
  'server.restart': 'Servidor reiniciat',
  'server.start': 'Servidor arrencat',
  'server.stop': 'Servidor aturat',
  'server.unlinked': 'Servidor desenllaçat',
  'session.code_rotated': 'Codi de sessió canviat',
  'session.command': 'Ordre enviada a una sessió',
  'session.command_withdrawn': 'Ordre retirada d’una sessió',
  'session.commander_cleared': 'Comandant tret d’una sessió',
  'session.commander_handed': 'Comandament passat a una altra persona',
  'session.commander_set': 'Comandant nomenat',
  'session.commander_unknown': 'Sessió sense comandant',
  'session.created': 'Sessió creada',
  'session.deleted': 'Sessió esborrada',
  'session.ended': 'Sessió acabada',
  'session.proposal_accepted': 'Proposta de sessió acceptada',
  'session.proposal_open': 'Proposta de sessió reoberta',
  'session.proposal_rejected': 'Proposta de sessió rebutjada',
  'session.server_changed': 'Servidor d’una sessió canviat',
  'session.started': 'Sessió arrencada',
  'session.vote_cast': 'Vot en una sessió',
  'session.vote_closed': 'Votació tancada',
  'session.vote_opened': 'Votació oberta',
  'settings.cba': 'Settings CBA canviats',
  'settings.discord_bot': 'Bot de Discord configurat',
  'settings.reference_server': 'Servidor de referència canviat',
  'settings.roadmap_refreshed': 'Full de ruta actualitzat a mà',
  'settings.roadmap_source': 'Origen del full de ruta canviat',
  'settings.server_passwords': 'Contrasenyes dels servidors canviades',
  'settings.sign_in': 'Portes d’entrada canviades',
  'terrain.rederived': 'Perfil de terreny tornat a derivar',
  'terrain.survey_done': 'Exploració de terreny acabada',
  'terrain.survey_paused': 'Exploracions de terrenys aturades',
  'terrain.survey_queued': 'Terrenys en cua per explorar',
  'terrain.survey_resumed': 'Exploracions de terrenys represes',
  'terrain.survey_started': 'Exploració de terreny començada',
  'test.claimed': 'Fitxa de prova agafada',
  'test.closed': 'Fitxa de prova tancada',
  'test.created': 'Fitxa de prova creada',
  'test.deleted': 'Fitxa de prova esborrada',
  'test.done': 'Fitxa de prova acabada',
  'test.mission_set': 'Missió triada per a una fitxa de prova',
  'test.mission_test_mode': 'Missió en mode de prova',
  'test.opened': 'Fitxa de prova oberta',
  'test.performer': 'Qui fa una fitxa de prova canviat',
  'test.server_freed': 'Servidor de prova alliberat',
  'test.server_started': 'Servidor de prova arrencat',
  'test.server_stopped': 'Servidor de prova aturat',
  'test.step_added': 'Pas afegit a una fitxa de prova',
  'test.step_edited': 'Pas d’una fitxa de prova contestat o editat',
  'test.step_removed': 'Pas tret d’una fitxa de prova',
  'watcher.connect': 'Vigilant de pantalla connectat',
  'watcher.disconnect': 'Vigilant de pantalla desconnectat',
};

/** The label of a kind, or null for one nobody wrote a label for (never the key). */
export function kindLabel(kind: string): string | null {
  return KIND_LABELS[kind] ?? null;
}

/** The area a kind belongs to: its first word, or the area it reads as. */
export function areaOf(kind: string): string {
  const head = kind.split('.')[0] ?? '';
  return AREA_ALIASES[head] ?? head;
}

/**
 * The kinds the filter offers, by area in the filter's order and by label inside each, with the
 * kinds that have no label last, in an area of their own: a filter never hides an event.
 */
export function kindGroups(
  kinds: readonly string[],
): { area: string; label: string; kinds: string[] }[] {
  const by = new Map<string, string[]>();
  for (const kind of kinds) {
    const area = areaOf(kind) in ACTIVITY_AREAS ? areaOf(kind) : '';
    by.set(area, [...(by.get(area) ?? []), kind]);
  }
  const order = [...Object.keys(ACTIVITY_AREAS), ''];
  return order
    .filter((area) => by.has(area))
    .map((area) => ({
      area,
      label: area === '' ? 'Altres accions' : ACTIVITY_AREAS[area]!,
      kinds: [...by.get(area)!].sort((a, b) =>
        (kindLabel(a) ?? a).localeCompare(kindLabel(b) ?? b, 'ca'),
      ),
    }));
}
